import { HttpParams, httpResource } from '@angular/common/http';
import { computed, inject, Injectable } from '@angular/core';
import { AnalysisConfigurationResponse } from '@api-response-types/analysis-configuration.response';
import { AnalysisExportResponse } from '@api-response-types/analysis-export.response';
import {
  AnalysisRowType,
  AnalysisVersionGroupResponse,
} from '@api-response-types/analysis-version-group.response';
import { AnalysisVersionResponse } from '@api-response-types/analysis-version.response';
import { AnalysisType } from '@api-types/enums/analysis-type.enum';
import { AnalysisProgressSseEventPayload } from '@api-types/sse/event-payloads/analysis-progress.sse-event-payload';
import { environment } from '@environments/environment';
import { Store } from '@ngrx/store';
import {
  selectAnalysisConfigurationIdByQueryParam,
  selectAnalysisFilter,
  selectAnalysisHierarchy,
  selectAnalysisVersionIdByQueryParam,
  selectConfigurationId,
} from 'demand-forecasting/store/router-state/router.selectors';

@Injectable({
  providedIn: 'root',
})
export class AnalysisStateService {
  private readonly store = inject(Store);
  private readonly configurationId = this.store.selectSignal(
    selectConfigurationId,
  );
  private readonly analysisConfigurationId = this.store.selectSignal(
    selectAnalysisConfigurationIdByQueryParam,
  );
  private readonly analysisVersionId = this.store.selectSignal(
    selectAnalysisVersionIdByQueryParam,
  );

  private readonly analysisHierarchy = this.store.selectSignal(
    selectAnalysisHierarchy,
  );

  private readonly analysisFilter =
    this.store.selectSignal(selectAnalysisFilter);

  // queries
  public configurations = httpResource<AnalysisConfigurationResponse[]>(() => {
    const configurationId = this.configurationId();
    if (configurationId === undefined) {
      return undefined;
    }
    return `${environment.api.url}/analysis/analysis-configurations/configurations/${configurationId}`;
  });

  public versions = httpResource<AnalysisVersionResponse[]>(() => {
    const analysisConfigurationId = this.analysisConfigurationId();
    if (analysisConfigurationId === undefined) {
      return undefined;
    }
    return `${environment.api.url}/analysis/analysis-configurations/${analysisConfigurationId}/versions`;
  });

  // highest aggregated view, always returns exactly one element
  public aggregatedVersionGroup = httpResource<[AnalysisVersionGroupResponse]>(
    () => {
      const analysisVersionId = this.analysisVersionId();
      if (analysisVersionId === undefined) {
        return undefined;
      }
      return `${environment.api.url}/analysis/analysis-versions/${analysisVersionId}/version-groups`;
    },
  );

  public tableVersionGroups = httpResource<AnalysisVersionGroupResponse[]>(
    () => {
      const analysisVersionId = this.analysisVersionId();
      if (analysisVersionId === undefined) {
        return undefined;
      }

      const hierarchy = this.analysisHierarchy();
      const filter = this.analysisFilter();

      const httpParams = new HttpParams({
        fromObject: {
          hierarchy,
          ...(filter ? { filter: JSON.stringify(filter) } : {}),
        },
      });

      return `${environment.api.url}/analysis/analysis-versions/${analysisVersionId}/version-groups?${httpParams.toString()}`;
    },
  );

  public exports = httpResource<AnalysisExportResponse[]>(() => {
    const analysisVersionId = this.analysisVersionId();
    if (analysisVersionId === undefined) {
      return undefined;
    }
    return `${environment.api.url}/analysis-exports?analysisVersionId=${analysisVersionId}`;
  });

  patchVersion(event: AnalysisProgressSseEventPayload): void {
    const currentVersions = this.versions.value();
    if (!currentVersions) {
      return;
    }

    const updatedVersions = currentVersions.map((version) =>
      version.id === event.id ? { ...version, ...event } : version,
    );

    this.versions.set(updatedVersions);
  }

  // selectors
  selectedVersion = computed(() => {
    if (!this.versions.hasValue()) {
      return undefined;
    }
    return this.versions.value().find((v) => v.id === this.analysisVersionId());
  });

  hasNoElapsedMonths = computed(() => {
    if (
      this.selectedVersion()?.analysisType !== AnalysisType.SINGLE_PERIOD ||
      !this.aggregatedVersionGroup.hasValue()
    ) {
      return false;
    }
    return !this.aggregatedVersionGroup
      .value()
      .some((row) => row.rowType === AnalysisRowType.TIMEPOINT);
  });

  selectedConfiguration = computed(() => {
    if (!this.configurations.hasValue()) {
      return undefined;
    }
    return this.configurations
      .value()
      .find((c) => c.id === this.analysisConfigurationId());
  });
}
